import React, { useMemo } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  SlotCapacityAffordance,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroAmbient,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroPhotoFloat.css";

/**
 * HeroPhotoFloat — the Photo Float family renderer. Text column
 * (heading + body + CTA) sits on the **left** half of the hero,
 * vertically centred. A small cluster of 3-4 photos floats on the
 * **right** half, each photo carrying its own staggered bob (per-
 * photo ``--hero-float-index`` drives ``animation-delay`` on the
 * shared ``hero-ambient-photo-float`` keyframe — see
 * ``heroModeAnimations.css``) so the cluster reads as a group of
 * independent floating objects sharing the air rather than a rigid
 * board pulsing in unison.
 *
 * The composition (text left / floating cluster right) and the
 * motion (per-photo staggered bob) are identical across every
 * member of the family. What changes is the **frame format** of
 * each photo, encoded in ``item.hero_mode``:
 *
 *   * ``photo_float_rounded`` — moderate ``border-radius`` + soft
 *                               drop shadow + thin border. The
 *                               "polite portfolio" finish: each
 *                               photo reads as a discrete framed
 *                               object hanging in space.
 *   * ``photo_float_circle``  — full ``border-radius: 50%`` mask,
 *                               slightly stronger shadow to lift
 *                               the orb off the background. Best
 *                               with portrait-oriented photos so
 *                               faces sit cleanly inside the
 *                               circle; landscape photos get
 *                               aggressively cropped by the mask.
 *   * ``photo_float_flush``   — same moderate ``border-radius`` as
 *                               the rounded sibling but no shadow
 *                               and no border. The "raw photo"
 *                               finish: each picture reads as a
 *                               printed image pinned in place
 *                               rather than a framed object — the
 *                               rounded corners stay so the
 *                               rectangle never reads as a hard,
 *                               unfinished crop.
 *   * ``photo_float_mixed``   — alternates per-cell chrome between
 *                               the rounded and circle finishes
 *                               (cards 0, 1 keep the rounded frame;
 *                               cards 2, 3 become circular orbs), so
 *                               the stack reads as rectangular
 *                               anchors with circular accents rather
 *                               than a uniform column.
 *
 * Per-photo positioning (the cluster shape): the photos stack in
 * NORMAL FLOW inside a centred flex-column panel — each card sits
 * under the previous one and a negative ``margin-top`` overlaps
 * them into one dense, big stack, with small left/right offsets for
 * a hand-arranged collage feel. Flow stacking (rather than absolute
 * ``top`` percentages of the panel) keeps the stack a definite,
 * self-contained height so it stays together and inside the hero no
 * matter how tall the hero/panel is. A descending z-index makes
 * each card sit over the one below it. Cards grow into the stack
 * gracefully with count (1 photo → just the top card; 2 → top two;
 * etc.). The desktop and mobile trays share this mechanism and
 * differ only in tuning (card width, overlap, offsets).
 */

// Mode-id constants for the family. Keep these in lockstep with
// the backend ``PhotoFloatMode`` / ``PhotoFloatCircleMode`` /
// ``PhotoFloatFlushMode`` / ``PhotoFloatMixedMode`` ids — the
// renderer keys off ``hero_mode`` to pick the per-photo CSS class
// that owns the chrome. Centralising the ids here lets a future
// addition (e.g. ``photo_float_polaroid``) land in this constant
// block first before touching the per-photo class lookup.
const MODE_FLOAT_CIRCLE = "photo_float_circle";
const MODE_FLOAT_FLUSH = "photo_float_flush";
const MODE_FLOAT_MIXED = "photo_float_mixed";

// Mixed-mode per-cell chrome chain. Indexed by the card's position
// in the stack: the top two cards (0, 1) keep the canonical rounded
// frame and the lower two (2, 3) flip to the circle mask. Pairing
// rectangles + circles (rather than e.g. alternating every other
// card) makes the stack read as a composed layout — rectangular
// anchors with circular accents — instead of a random alternation.
// See the ``photo_float_mixed`` rationale in
// ``HeroPhotoFloatMixedMode`` (backend ``definitions.py``). Empty
// string = no chrome modifier (canonical rounded).
const MIXED_CHROME_BY_INDEX = ["", "", "circle", "circle"];

// ---------------------------------------------------------------------------
// Per-breakpoint tuning
// ---------------------------------------------------------------------------
//
// ``contentPx`` / ``contentPy`` — outer padding for the text
//   column on the left half.
// ``rightPanelPct`` — width of the right-half floating-cluster
//   panel as a fraction of the hero width (mobile collapses to
//   100% via the CSS-only stacking branch — see the ``isStacked``
//   handling).
// ``photoMaxWidth`` — MOBILE photo cap in pixels (mobile cards size
//   as a % of the full-width tray, so they need an upper cap). On
//   desktop the cards use ``photoWidthPx`` instead (see below).
// ``photoWidthPx`` (desktop only) — FIXED pixel width for each card.
//   Desktop deliberately uses an absolute width (not a % of the
//   panel) so the photos — and therefore the whole stack's height —
//   do NOT grow as the hero / window widens. The height stays put;
//   a wider hero just leaves more empty air around the cluster.
// ``overlapPx`` (desktop only) — FIXED negative ``margin-top`` (px)
//   pulling each card up over the one above it. Fixed (not a % of
//   the panel) for the same reason as ``photoWidthPx`` — the
//   vertical overlap, and so the stack height, stays constant as the
//   hero widens. The larger this is, the closer the side-by-side
//   cards sit in height.
// ``offsetsPx`` (desktop only) — FIXED per-card ``margin-left`` (px)
//   that nudges each card left / right of centre for the collage
//   feel. Fixed px (not % of panel) so the cards don't drift further
//   apart — and risk overflowing — as the hero widens. Wider
//   breakpoints simply ship bigger offsets here so their bigger
//   cards still read as a hand-arranged, side-pushed collage.
// ``stackedHeroMinHeight`` — minimum mobile hero height. Mobile
//   renders photos one below another, so the hero needs a taller
//   natural floor than the desktop side-by-side composition.
const BP_CONFIG = {
  extra_large: { contentPx: 64, contentPy: 56, rightPanelPct: 48, photoMaxWidth: 460, stackedHeroMinHeight: 0, photoWidthPx: 320, overlapPx: -230, offsetsPx: [320, -320, 346, -262] },
  large:       { contentPx: 56, contentPy: 48, rightPanelPct: 50, photoMaxWidth: 420, stackedHeroMinHeight: 0, photoWidthPx: 280, overlapPx: -202, offsetsPx: [280, -280, 302, -230] },
  medium:      { contentPx: 40, contentPy: 40, rightPanelPct: 52, photoMaxWidth: 360, stackedHeroMinHeight: 0, photoWidthPx: 240, overlapPx: -173, offsetsPx: [120, -120, 150, -72] },
  small:       { contentPx: 24, contentPy: 32, rightPanelPct: 100, photoMaxWidth: 190, stackedHeroMinHeight: 980 },
  extra_small: { contentPx: 16, contentPy: 24, rightPanelPct: 100, photoMaxWidth: 168, stackedHeroMinHeight: 860 },
};

// Cap defensively against the backend ``PhotoFloatMode._PHOTO_MAX``
// (= 4). The cluster layout chain is sized for exactly 4 cells —
// adding a 5th would overflow into a second row of cells the
// renderer doesn't have geometry for, and would crowd the per-
// photo bob delays to the point that adjacent photos bob nearly
// in sync (the per-photo independence is what the family promises).
const MAX_PHOTOS = 4;

// ---------------------------------------------------------------------------
// Desktop cascade (normal-flow stacking)
// ---------------------------------------------------------------------------
//
// The desktop cluster uses the SAME mechanism as the mobile tray:
// the photos stack in normal document flow (not absolute ``top``
// positioning), vertically centred inside the right panel, and a
// negative ``margin-top`` overlaps each card onto the one above it.
//
// Why flow instead of absolute ``top`` percentages: a photo's
// HEIGHT is derived from its WIDTH (``width`` % + the 4:5
// ``aspect-ratio``), but an absolute ``top`` percentage resolves
// against the PANEL HEIGHT. On a tall hero those two are decoupled,
// so the lower cards were positioned far down the tall panel and
// spilled way below the hero. In normal flow the stack has a
// definite, self-contained height: each card sits directly under
// the previous one (minus the overlap), so the whole cluster stays
// together and within the hero no matter how tall the panel is.
//
//   * SIZE — each card is a FIXED ``photoWidthPx`` (per breakpoint),
//     NOT a percentage of the panel. Fixed px means the photos — and
//     so the stack's height — stay the same size as the hero widens
//     instead of scaling up and forcing the hero taller.
//   * CLOSE TOGETHER — ``overlapPx`` (a fixed negative
//     ``margin-top``) pulls each card up over the one above it.
//     Fixed px keeps the overlap (and the stack height) constant as
//     the hero widens; a bigger value sits the cards closer in
//     height.
//   * COLLAGE FEEL — ``offsetsPx`` nudges each card left or right of
//     centre (fixed px) so the stack reads as a hand-arranged
//     collage. Wider breakpoints ship bigger offsets so their bigger
//     cards still push toward the sides without drifting off as the
//     hero widens.
//
// All three live per-breakpoint in ``BP_CONFIG`` above. Mobile keeps
// its own (% width + small px overlap) tuning via the constants
// below.

// Mobile keeps the photos in normal vertical flow so the hero grows
// naturally, but alternating margins and overlap preserve the "floating
// collage" feel instead of reading like a plain image list. Use margins
// instead of transform because the ambient float animation also owns
// transform on the photo cell.
const STACKED_MOBILE_OFFSETS = [
  { marginLeft: "-18%", marginRight: "0%" },
  { marginLeft: "14%", marginRight: "-14%" },
  { marginLeft: "-12%", marginRight: "0%" },
  { marginLeft: "18%", marginRight: "-18%" },
];
const STACKED_MOBILE_OVERLAP_PX = -12;

// ---------------------------------------------------------------------------
// HeroPhotoFloat main renderer
// ---------------------------------------------------------------------------

const HeroPhotoFloat = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const gallerySlotData = heroSlots?.getSlot("gallery");
  const galleryItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const images = useMemo(
    () => galleryItems.filter((it) => it?.type === "image").slice(0, MAX_PHOTOS),
    [galleryItems],
  );
  const hasImages = images.length > 0;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const modeId = item?.hero_mode;

  // Resolve the per-photo chrome modifier from the mode id. The
  // canonical ``photo_float_rounded`` doesn't add a modifier (the
  // base ``hero-photo-float-cell`` already carries the rounded
  // chrome — corner radius, soft shadow, thin border); the
  // sibling ids each layer their own modifier on top to flip the
  // chrome story. ``photo_float_mixed`` is the only family member
  // whose modifier varies per cell (resolved inside the cells loop
  // via ``MIXED_CHROME_BY_INDEX``); the others apply a single
  // modifier across every cell so the cluster reads with one
  // consistent chrome story. Same dispatch pattern Photo Banner
  // uses for its flush variants — picking the modifier from the
  // id keeps the CSS rules scoped to the right sibling without
  // per-mode selector duplication.
  let uniformChromeModifier = "";
  if (modeId === MODE_FLOAT_CIRCLE) {
    uniformChromeModifier = " hero-photo-float-cell--circle";
  } else if (modeId === MODE_FLOAT_FLUSH) {
    uniformChromeModifier = " hero-photo-float-cell--flush";
  }
  const isMixed = modeId === MODE_FLOAT_MIXED;

  // Mobile (extra_small / small): collapse to a centered vertical
  // layout — text on top, then photos one below another. The
  // ``rightPanelPct: 100`` entries in ``BP_CONFIG`` for those
  // breakpoints already signal "the panel takes the full width"
  // geometrically; the boolean here picks both the flow direction
  // and the per-cell positioning branch.
  const isStacked = bp === "extra_small" || bp === "small";

  // Ambient class — the family canonical ``photo_float_rounded``
  // declares ``ambient_animation = "photo-float"`` and the
  // siblings inherit it unchanged. Returns "" under
  // ``prefers-reduced-motion`` so the cluster sits perfectly
  // still in that mode (the per-photo ``--hero-float-index``
  // CSS variable stays set on the DOM but the keyframe doesn't
  // run, so the cluster lands in its rest position).
  const ambientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });

  // Build the per-photo cells once. Each cell carries its index,
  // which drives the per-photo bob stagger (via ``--hero-float-
  // index``), the stack order (z-index), and the per-card overlap /
  // offset lookups in the cells loop below.
  //
  // Hoisted ABOVE the empty-state early return so this hook always
  // executes in the same order regardless of whether the gallery
  // slot is populated — React's rules-of-hooks forbid conditional
  // hook calls. Building the empty array in the no-images branch
  // is cheap and the descriptor is only consumed inside the
  // populated branch below.
  const cells = useMemo(
    () => images.map((img, idx) => ({ img, idx })),
    [images],
  );

  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  // Empty state mirrors Photo Banner / Photo Drift: stack the
  // text centred so the editor still has something readable while
  // the user wires up images. The ``+ Add Image`` affordance is
  // surfaced inline so editors can populate the slot from the
  // canvas.
  if (!hasImages) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  // The text + cluster sit inside a single flex container that
  // either flows row (desktop) or column (mobile). On desktop the
  // text column hugs ``flex: 1 1 0`` so it absorbs whatever's left
  // after the right-side cluster panel claims its share via
  // ``flex-basis: rightPanelPct%``. On mobile both children stack
  // vertically in normal flow so the hero can grow tall enough for
  // one centered photo per row.
  const rootStyle = {
    ...containerStyle,
    ...fullBleedStyle,
    ...(isStacked ? { minHeight: `${config.stackedHeroMinHeight}px` } : {}),
  };
  const layoutStyle = isStacked
    ? { minHeight: `${config.stackedHeroMinHeight}px` }
    : responsiveHeight
      ? { minHeight: `${baseHeightPx}px` }
      : undefined;

  // Build the per-photo cell elements once. Both desktop and mobile
  // stack the photos in NORMAL FLOW inside the panel (the panel is a
  // centred flex column): each card sits under the previous one and
  // a negative ``margin-top`` overlaps them. Desktop and mobile only
  // differ in the tuning constants (card width, overlap amount, and
  // left/right offsets). See the desktop-cascade rationale block by
  // ``DESKTOP_CELL_WIDTH_PCT`` for why flow beats absolute ``top``
  // positioning here.
  const cellEls = cells.map(({ img, idx }) => {
    const src = img.cropped_image_url || img.image_url;
    // For the mixed sibling each cell picks its chrome from the
    // per-index chain so the cluster alternates rounded ↔ circle.
    // For the uniform siblings every cell shares the same modifier
    // (or none, for canonical rounded). Wrap-around via ``%`` keeps
    // the lookup safe regardless of the photo count.
    let cellChromeModifier = uniformChromeModifier;
    if (isMixed) {
      const mixedKey =
        MIXED_CHROME_BY_INDEX[idx % MIXED_CHROME_BY_INDEX.length];
      cellChromeModifier = mixedKey
        ? ` hero-photo-float-cell--${mixedKey}`
        : "";
    }
    // Mobile reads its left/right collage nudges from the %-based
    // ``STACKED_MOBILE_OFFSETS`` chain; desktop reads fixed-px nudges
    // from ``config.offsetsPx`` (see ``BP_CONFIG``).
    const mobileOffset = isStacked
      ? STACKED_MOBILE_OFFSETS[idx % STACKED_MOBILE_OFFSETS.length]
      : null;
    const desktopOffsetPx = isStacked
      ? null
      : config.offsetsPx[idx % config.offsetsPx.length];
    // Overlap: mobile uses a small fixed pixel pull; desktop uses a
    // fixed pixel pull too (``config.overlapPx``) so the overlap — and
    // the stack height — stays constant as the hero widens. The first
    // card never gets a negative top margin — nothing sits above it.
    const overlapMarginTop =
      idx === 0
        ? undefined
        : isStacked
          ? `${STACKED_MOBILE_OVERLAP_PX}px`
          : `${config.overlapPx}px`;
    return (
      <div
        key={`float-${img.id || idx}-${idx}`}
        className={`hero-photo-float-cell${cellChromeModifier} ${ambientClass} relative`}
        style={{
          // Normal-flow stacking for every breakpoint — the photos
          // flow one below another inside the centred panel column,
          // overlapping via ``marginTop``. ``position: relative``
          // overrides the base ``.hero-photo-float-cell`` absolute
          // rule so the cards participate in flow (and the hero can
          // never be left with cards spilling far below it).
          position: "relative",
          // Mobile: % of the full-width tray, capped in px. Desktop:
          // a fixed px width (capped to the panel via ``maxWidth:
          // 100%``) so the photo never grows as the hero widens.
          width: isStacked ? "72%" : `${config.photoWidthPx}px`,
          maxWidth: isStacked ? `${config.photoMaxWidth}px` : "100%",
          marginTop: overlapMarginTop,
          marginLeft: isStacked ? mobileOffset.marginLeft : `${desktopOffsetPx}px`,
          marginRight: isStacked ? mobileOffset.marginRight : "0px",
          // Cards higher in the stack sit on top of the ones below,
          // so each card overlaps the next — the "stack peeling
          // downward" reading.
          zIndex: cells.length - idx,
          // Per-photo bob stagger — ``--hero-float-index`` is read by
          // the ``hero-ambient-photo-float`` keyframe's
          // ``animation-delay`` calc(). ``--hero-float-count`` lets
          // the keyframe normalise the delay distribution to the
          // actual photo count.
          "--hero-float-index": idx,
          "--hero-float-count": cells.length,
        }}
      >
        {src ? (
          (() => {
            const { className: filterClass, style: filterStyle } =
              getHeroImageStyle(img);
            return (
              <ProgressiveHeroImage
                src={src}
                alt={img.alt_text || ""}
                loading={idx === 0 ? "eager" : "lazy"}
                draggable={false}
                className={`hero-photo-float-img ${filterClass}`.trim()}
                style={filterStyle}
              />
            );
          })()
        ) : (
          <div className="hero-photo-float-img hero-photo-float-img--empty" />
        )}
        <EditableSlotMediaOverlay slotData={img} heroItem={item} />
      </div>
    );
  });

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={rootStyle}
    >
      {heroBackgroundLayer}
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`flex w-full ${isStacked ? "flex-col" : "flex-row"} ${
          isStacked || responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        style={layoutStyle}
      >
        {/* Text column — left half on desktop, top on mobile.
            ``min-w-0`` mirrors Split / Photo Banner to protect
            against intrinsic-wide children blowing past the
            visible width. ``flex-1`` lets the column absorb the
            remaining row space on desktop. */}
        <div
          ref={contentAnim.ref}
          className={`flex flex-col justify-center gap-4 flex-1 min-w-0 ${
            isStacked ? "items-center text-center" : ""
          } ${contentAnim.className}`}
          style={{
            paddingLeft: config.contentPx,
            paddingRight: config.contentPx,
            paddingTop: config.contentPy,
            paddingBottom: config.contentPy,
            ...contentAnim.style,
          }}
        >
          <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
          <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
          <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
        </div>

        {/* Floating cluster panel — right half on desktop, bottom
            tray on mobile. It is a centred flex column on every
            breakpoint: the photos stack in normal flow (overlapping
            via negative ``margin-top``) and ``justify-center`` keeps
            the stack vertically centred in the panel so it stays
            aligned with the text column and inside the hero, however
            tall the hero is. (Earlier versions positioned the cards
            absolutely by ``top`` percentage of this panel, which on
            a tall hero pushed the lower cards far below the hero.) */}
        <div
          className={`hero-photo-float-panel relative flex-shrink-0 flex flex-col items-center ${
            isStacked ? "gap-0 pb-8" : "justify-center"
          }`}
          style={
            isStacked
              ? { width: "100%" }
              : { width: `${config.rightPanelPct}%`, alignSelf: "stretch" }
          }
        >
          {cellEls}
          <SlotCapacityAffordance
            heroSlots={heroSlots}
            heroId={item?.id}
            slotName="gallery"
            currentCount={images.length}
            className={isStacked ? "w-full max-w-xs px-4" : "absolute inset-x-6 bottom-6"}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroPhotoFloat);
